/**
 * Staff-facing procedure catalog (who / when / what record).
 * Titles only — not signed QMS prose. Mirrors docs/sops/README.md book IDs.
 */

export type SopFamily = 'inbound' | 'sell' | 'quality' | 'docs' | 'intake' | 'later';

export type SopCatalogEntry = {
  id: string;
  /** Short label for inline chips (timeline, hints). */
  shortTitleFr: string;
  titleFr: string;
  titleEn: string;
  ownerFr: string;
  triggerFr: string;
  recordFr: string;
  family: SopFamily;
  reqIds?: readonly string[];
  /** Named-later / P3 — still listed for reference. */
  later?: boolean;
};

export const SOP_FAMILY_LABEL_FR: Record<SopFamily, string> = {
  inbound: 'Entrées / stock',
  sell: 'Vente / parties',
  quality: 'Qualité / rappel',
  docs: 'Documents / argent / personnes',
  intake: 'Preuves externes',
  later: 'Prévu plus tard',
};

export const SOP_CATALOG: readonly SopCatalogEntry[] = [
  {
    id: 'SOP-SUP-01',
    shortTitleFr: 'Qualification fournisseur',
    titleFr: 'Qualifier le fournisseur avant un BC de routine',
    titleEn: 'Qualify supplier before routine PO',
    ownerFr: 'Ops / commercial',
    triggerFr: 'Nouveau fournisseur avant le premier achat de routine',
    recordFr: 'Statut de qualification + trace de diligence sur le fournisseur',
    family: 'inbound',
    reqIds: ['REQ-BUY-01'],
  },
  {
    id: 'SOP-SUP-03',
    shortTitleFr: 'Requalification fournisseur',
    titleFr: 'Requalifier le fournisseur sur un cycle',
    titleEn: 'Re-qualify supplier on a cycle',
    ownerFr: 'Ops / commercial',
    triggerFr: 'Recheck périodique ou événement de risque',
    recordFr: 'Statut de qualification mis à jour + échéance',
    family: 'inbound',
    reqIds: ['REQ-BUY-01'],
  },
  {
    id: 'SOP-RCV-01',
    shortTitleFr: 'Réception vs BC',
    titleFr: 'Réceptionner vs BC ; lot / péremption ; quarantaine jusqu’à libération',
    titleEn: 'Receive vs PO; lot/expiry; quarantine until release',
    ownerFr: 'Entrepôt / ops',
    triggerFr: 'Marchandises arrivées',
    recordFr: 'Réception : lot, péremption, qté, coût, emplacement quarantaine (ou libéré)',
    family: 'inbound',
    reqIds: ['REQ-BUY-02', 'REQ-STK-01'],
  },
  {
    id: 'SOP-REL-01',
    shortTitleFr: 'Libération vendable',
    titleFr: 'Libérer vers le vendable',
    titleEn: 'Release-to-saleable',
    ownerFr: 'Autorité nommée (RP-style)',
    triggerFr: 'Contrôles de libération après entrée',
    recordFr: 'Acte de libération + acteur ; qté entre en vendable / ATP',
    family: 'inbound',
    reqIds: ['REQ-BUY-02', 'REQ-AUTH-01'],
  },
  {
    id: 'SOP-STK-01',
    shortTitleFr: 'Ajustement stock',
    titleFr: 'Ajuster / transférer avec motif + acteur',
    titleEn: 'Adjust / transfer with reason + actor',
    ownerFr: 'Entrepôt / ops',
    triggerFr: 'Correction de quantité ou d’emplacement',
    recordFr: 'Mouvement avec motif + acteur ; pas de négatif silencieux',
    family: 'inbound',
    reqIds: ['REQ-AUTH-01'],
  },
  {
    id: 'SOP-STK-03',
    shortTitleFr: 'Ségrégation stock',
    titleFr: 'Séparer quarantaine, vendable, retour, périmé',
    titleEn: 'Segregate quarantine, saleable, return, expired',
    ownerFr: 'Entrepôt / ops',
    triggerFr: 'Marchandises entrent ou changent de classe',
    recordFr: 'Emplacement / statut aligné sur la classe ; vendable seulement après libération',
    family: 'inbound',
    reqIds: ['REQ-STK-01', 'REQ-STK-02'],
  },
  {
    id: 'SOP-PCK-01',
    shortTitleFr: 'Prélèvement avec lot',
    titleFr: 'Prélever / expédier avec lot sur la ligne',
    titleEn: 'Pick / ship expiry goods; lot on the line',
    ownerFr: 'Entrepôt / ops',
    triggerFr: 'Confirmer → expédier',
    recordFr: 'Lignes d’expédition avec lot ; FEFO plus tard (SOP-PCK-02)',
    family: 'inbound',
    reqIds: ['REQ-SEL-01', 'REQ-STK-03'],
  },
  {
    id: 'SOP-TSP-01',
    shortTitleFr: 'Conditions d’expédition',
    titleFr: 'Expédier sous conditions et documents déclarés',
    titleEn: 'Ship under stated conditions and documents',
    ownerFr: 'Entrepôt / ops',
    triggerFr: 'Expédition vers acheteur ou transporteur',
    recordFr: 'Flags conditions / docs sur l’expédition ; mêmes faits que le BL',
    family: 'inbound',
    reqIds: ['REQ-SEL-02'],
  },
  {
    id: 'SOP-CUS-01',
    shortTitleFr: 'Qualification acheteur',
    titleFr: 'Qualifier l’acheteur pro avant une vente de routine',
    titleEn: 'Qualify professional buyer before routine sale',
    ownerFr: 'Commercial',
    triggerFr: 'Nouvel acheteur avant la première confirmation de routine',
    recordFr: 'Statut d’habilitation / qualification sur la partie',
    family: 'sell',
    reqIds: ['REQ-PAR-01'],
  },
  {
    id: 'SOP-SEL-01',
    shortTitleFr: 'Confirmation commande',
    titleFr: 'Confirmer la commande sur un livre de prix nommé',
    titleEn: 'Confirm order on a named price book',
    ownerFr: 'Commercial',
    triggerFr: 'Devis accepté ou commande prête à réserver',
    recordFr: 'Acte de confirmation + livre de prix ; réserve pour prélèvement',
    family: 'sell',
    reqIds: ['REQ-PRC-01', 'REQ-SEL-01'],
  },
  {
    id: 'SOP-PAR-01',
    shortTitleFr: 'Lead partenaire',
    titleFr: 'Lead partenaire / marque → Fournisseur',
    titleEn: 'Partner / brand lead qualify → Supplier',
    ownerFr: 'Commercial BD',
    triggerFr: 'Lead partenaire depuis la surface commerciale',
    recordFr: 'Lead → qualification → Fournisseur (PARTNER) ; lien conservé',
    family: 'sell',
    reqIds: ['REQ-PAR-04'],
  },
  {
    id: 'SOP-BUY-01',
    shortTitleFr: 'Lead acheteur',
    titleFr: 'Lead acheteur → partie → devis',
    titleEn: 'Buyer lead → party → devis',
    ownerFr: 'Commercial',
    triggerFr: 'Lead intention acheteur',
    recordFr: 'Lead → client / B2B → handoff devis',
    family: 'sell',
    reqIds: ['REQ-PAR-03'],
  },
  {
    id: 'SOP-ENQ-01',
    shortTitleFr: 'Demande commerciale',
    titleFr: 'Répondre à une demande acheteur / fournisseur (hors plainte)',
    titleEn: 'Answer buyer or supplier enquiry (not a complaint)',
    ownerFr: 'Commercial',
    triggerFr: 'Question sur commande, lot, promesse ou facture',
    recordFr: 'Dossier lié à la partie + commande / facture ; handoff si besoin',
    family: 'sell',
    reqIds: ['REQ-SIG-01', 'REQ-SIG-02'],
  },
  {
    id: 'SOP-RET-01',
    shortTitleFr: 'Retour client',
    titleFr: 'Retour non vendable tant que l’évaluation n’est pas faite',
    titleEn: 'Return not saleable until assessment',
    ownerFr: 'Ops / RP-style',
    triggerFr: 'Retour client',
    recordFr: 'Évaluation + disposition (vendable | destruction | quarantaine)',
    family: 'quality',
    reqIds: ['REQ-QAL-02'],
  },
  {
    id: 'SOP-REC-01',
    shortTitleFr: 'Rappel de lot',
    titleFr: 'Arrêt de lot + liste clients',
    titleEn: 'Lot stop + customer list',
    ownerFr: 'Ops / RP-style',
    triggerFr: 'Rappel ou quarantaine d’un lot',
    recordFr: 'lot_stop ; liste contact, lot, qté depuis expéditions ; notifier',
    family: 'quality',
    reqIds: ['REQ-QAL-01'],
  },
  {
    id: 'SOP-FAL-01',
    shortTitleFr: 'Produit suspect',
    titleFr: 'Bloquer les biens suspectés falsifiés ou détournés',
    titleEn: 'Hold suspected falsified or diverted goods',
    ownerFr: 'Ops / RP-style',
    triggerFr: 'Suspicion de produit falsifié ou détourné',
    recordFr: 'Séparer du vendable ; dossier ; pas de libération avant disposition',
    family: 'quality',
  },
  {
    id: 'SOP-CMP-01',
    shortTitleFr: 'Plainte',
    titleFr: 'Dossier de plainte (produit vs service)',
    titleEn: 'Complaint file (product vs service)',
    ownerFr: 'Ops / commercial',
    triggerFr: 'Plainte reçue',
    recordFr: 'Récit + produit / lot / SKU ; flag produit vs service',
    family: 'quality',
  },
  {
    id: 'SOP-SUE-01',
    shortTitleFr: 'SUE cosmétique',
    titleFr: 'Parcours SUE cosmétique',
    titleEn: 'Cosmetic SUE path',
    ownerFr: 'Ops / conseil',
    triggerFr: 'Effet grave possible sur la santé (cosmétique)',
    recordFr: 'Dossier : lot + GTIN + réf. commande / facture ; intention de notifier',
    family: 'quality',
    reqIds: ['REQ-QAL-03'],
  },
  {
    id: 'SOP-DOC-01',
    shortTitleFr: 'Identité document',
    titleFr: 'Identité BC / BL / facture, numérotation, réimpression',
    titleEn: 'BC / BL / facture identity, numbering, reprint',
    ownerFr: 'Ops / finance',
    triggerFr: 'Envoi BC, expédition, facture',
    recordFr: 'Identité document ; numéros facture sans trou ; réimpression ; conservation',
    family: 'docs',
    reqIds: ['REQ-DOC-01'],
  },
  {
    id: 'SOP-ACT-01',
    shortTitleFr: 'Droits d’action',
    titleFr: 'Qui peut recevoir / confirmer / expédier / libérer / rappeler',
    titleEn: 'Who may receive / confirm / ship / release / recall',
    ownerFr: 'Admin / RP-style',
    triggerFr: 'Changement de rôle ou premier droit',
    recordFr: 'Packs de capacités ; acteur sur chaque écriture',
    family: 'docs',
    reqIds: ['REQ-AUTH-01'],
  },
  {
    id: 'SOP-MON-01',
    shortTitleFr: 'Clôture de période',
    titleFr: 'Pack de clôture de période pour le comptable',
    titleEn: 'Period close pack for accountant',
    ownerFr: 'Finance',
    triggerFr: 'Fin de mois / période',
    recordFr: 'Journaux, balance, récap TVA, exports nommés',
    family: 'docs',
    reqIds: ['REQ-MON-01'],
  },
  {
    id: 'SOP-PRF-01',
    shortTitleFr: 'Preuves d’import',
    titleFr: 'Preuves d’import / catégorie — dépôt + gate',
    titleEn: 'Import / category proofs store + gate',
    ownerFr: 'Ops / conseil',
    triggerFr: 'Dossier demandé ou gate d’import',
    recordFr: 'Fichier preuve + dates de validité ; pass/fail catalogue / achat',
    family: 'intake',
    reqIds: ['REQ-OUT-01'],
  },
  {
    id: 'SOP-PCK-02',
    shortTitleFr: 'Prélèvement FEFO',
    titleFr: 'Recommandation de prélèvement FEFO vs FIFO aveugle',
    titleEn: 'FEFO pick recommendation vs blind FIFO',
    ownerFr: 'Entrepôt / ops',
    triggerFr: 'Politique sur SOP-PCK-01',
    recordFr: 'Chrome de politique (plus tard)',
    family: 'later',
    later: true,
    reqIds: ['REQ-STK-03'],
  },
  {
    id: 'SOP-STK-02',
    shortTitleFr: 'Zone température',
    titleFr: 'Zone température sur emplacement (données)',
    titleEn: 'Temp-zone on location (data)',
    ownerFr: 'Entrepôt / ops',
    triggerFr: 'Données d’emplacement ; logs hors produit BMS',
    recordFr: 'Champ zone (plus tard)',
    family: 'later',
    later: true,
    reqIds: ['REQ-STK-04'],
  },
  {
    id: 'SOP-SUP-02',
    shortTitleFr: 'Contrôle 3PL',
    titleFr: 'Champs de contrôle écrit 3PL externalisé',
    titleEn: 'Outsourced 3PL written-control fields',
    ownerFr: 'Ops / RP-style',
    triggerFr: 'Contrôle GDP Ch 7',
    recordFr: 'Champs de contrôle (plus tard)',
    family: 'later',
    later: true,
    reqIds: ['REQ-BUY-03'],
  },
  {
    id: 'SOP-QAL-01',
    shortTitleFr: 'Auto-inspection',
    titleFr: 'Hook d’auto-inspection',
    titleEn: 'Self-inspection record hook',
    ownerFr: 'RP-style',
    triggerFr: 'Cycle d’auto-inspection (GDP Ch 8)',
    recordFr: 'Enregistrement (plus tard)',
    family: 'later',
    later: true,
    reqIds: ['REQ-QAL-04'],
  },
] as const;

const BY_ID = new Map(SOP_CATALOG.map((s) => [s.id, s]));

export function getSop(id: string): SopCatalogEntry | undefined {
  const key = String(id ?? '').trim().toUpperCase();
  return BY_ID.get(key);
}

export function listSops(opts?: { family?: SopFamily; includeLater?: boolean }): SopCatalogEntry[] {
  const includeLater = opts?.includeLater ?? true;
  return SOP_CATALOG.filter((s) => {
    if (!includeLater && s.later) return false;
    if (opts?.family && s.family !== opts.family) return false;
    return true;
  });
}

export function filterSops(
  query: string,
  opts?: { family?: SopFamily | ''; includeLater?: boolean },
): SopCatalogEntry[] {
  const q = query.trim().toLowerCase();
  const family = opts?.family || undefined;
  return listSops({
    family: family as SopFamily | undefined,
    includeLater: opts?.includeLater,
  }).filter((s) => {
    if (!q) return true;
    const hay = [
      s.id,
      s.shortTitleFr,
      s.titleFr,
      s.titleEn,
      s.ownerFr,
      s.triggerFr,
      s.recordFr,
      ...(s.reqIds ?? []),
    ]
      .join(' ')
      .toLowerCase();
    return hay.includes(q);
  });
}

export function isSopId(value: string): boolean {
  return /^SOP-[A-Z]{2,4}-\d{2}$/i.test(String(value ?? '').trim());
}
